import {
  Bell,
  Calendar,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  notificationService,
  type NotificationListResponse,
} from "../../services/notification.service";
import { useAuthStore } from "../../stores/authStore";

const PAGE_SIZE = 20;

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
  { value: "success", label: "Success" },
  { value: "info", label: "Info" },
];

const TYPE_COLORS: Record<string, string> = {
  warning: "bg-amber-400",
  error: "bg-red-400",
  success: "bg-emerald-400",
  info: "bg-blue-400",
};

const TYPE_BADGES: Record<string, string> = {
  warning: "bg-amber-50 text-amber-700 border border-amber-200",
  error: "bg-red-50 text-red-700 border border-red-200",
  success: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  info: "bg-blue-50 text-blue-700 border border-blue-200",
};

function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(dateStr));
}

function formatDateForInput(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Filters
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(0);

  const queryKey = ["notifications-page", { type, from, to, unreadOnly, page }];

  const { data, isLoading } = useQuery<NotificationListResponse>({
    queryKey,
    queryFn: () =>
      notificationService.list({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        type: type || undefined,
        from: from || undefined,
        to: to || undefined,
        unreadOnly: unreadOnly || undefined,
      }),
    enabled: isAuthenticated,
  });

  const notifications = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Mark single as read
  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-page"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
    },
  });

  // Mark all as read
  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-page"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({
        queryKey: ["notifications", "unread-count"],
      });
    },
  });

  const handleMarkRead = useCallback(
    (id: string) => markReadMutation.mutate(id),
    [markReadMutation],
  );

  const clearFilters = () => {
    setType("");
    setFrom("");
    setTo("");
    setUnreadOnly(false);
    setPage(0);
  };

  const hasFilters = type || from || to || unreadOnly;

  // Quick date presets
  const setDatePreset = (preset: "today" | "week" | "month") => {
    const now = new Date();
    const toStr = formatDateForInput(now);
    setTo(toStr);
    if (preset === "today") {
      setFrom(toStr);
    } else if (preset === "week") {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      setFrom(formatDateForInput(d));
    } else {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      setFrom(formatDateForInput(d));
    }
    setPage(0);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-100 rounded-xl">
              <Bell className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
              <p className="text-sm text-gray-500">
                {total} notification{total !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => markAllReadMutation.mutate()}
          disabled={markAllReadMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <CheckCheck className="w-4 h-4" />
          Mark all read
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Type filter */}
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(0);
            }}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Date from */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(0);
              }}
              placeholder="From date"
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Date to */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(0);
              }}
              placeholder="To date"
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          {/* Unread only toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => {
                setUnreadOnly(e.target.checked);
                setPage(0);
              }}
              className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
            />
            Unread only
          </label>
        </div>

        {/* Quick presets */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setDatePreset("today")}
            className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600 transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => setDatePreset("week")}
            className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600 transition-colors"
          >
            Last 7 days
          </button>
          <button
            onClick={() => setDatePreset("month")}
            className="text-xs px-3 py-1 rounded-full bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600 transition-colors"
          >
            Last 30 days
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Bell className="w-10 h-10 text-gray-200 mb-3" />
            <p className="text-sm text-gray-400 font-medium">
              {hasFilters
                ? "No notifications match your filters"
                : "No notifications yet"}
            </p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => {
                  if (!n.read) handleMarkRead(n.id);
                }}
                className={`px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  !n.read ? "bg-teal-50/40" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                      TYPE_COLORS[n.type] || "bg-gray-400"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p
                        className={`text-sm leading-tight ${
                          !n.read
                            ? "font-semibold text-gray-900"
                            : "font-medium text-gray-700"
                        }`}
                      >
                        {n.title}
                      </p>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${
                          TYPE_BADGES[n.type] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {n.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 leading-snug">
                      {n.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1.5 font-medium">
                      {formatDate(n.createdAt)}
                    </p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs font-medium text-gray-600 px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
